import { DialogActions, DialogTitle } from "@material-ui/core";
import React, { useEffect } from "react";
import styled from "styled-components";
import type { User as FirebaseUser } from "firebase/auth";

import Analytics from "js/analytics";
import { Button } from "js/Components/Button";
import { getStaticUrl } from "js/config";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { t } from "js/i18n";
import { BeautifulDialog, DialogContent, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap30 } from "js/react/components/Gap";
import { DefaultPaymentMethod } from "js/react/views/Billing/Checkout/components/PaymentForm";
import ChangePaymentMethodDialog from "js/react/views/Billing/dialogs/ChangePaymentMethodDialog";
import { InviteSection, SeatsInfoVariant } from "js/react/views/Billing/dialogs/components/InviteSection";
import getLogger, { LogGroup } from "js/core/logger";
import { useInviteMembersWithBilling } from "js/react/views/Billing/dialogs/hooks/useInviteMembersWithBilling";
import { settingsLabel } from "js/textStyles";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import ux from "js/core/services/userExperiences";

const logger = getLogger(LogGroup.BILLING);

const StyledDialogTitle = styled(DialogTitle)`
    h2 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    &&& {
        justify-content: space-between;
    }
`;

const SectionTitle = styled.div`
    ${settingsLabel}
`;

export interface InviteUsersDialogProps {
    cancelButtonText?: string;
    showSelf?: boolean;
    seatsInfoVariant?: SeatsInfoVariant;
    /** Post-/checkout invite: fill purchased seats only, no extra-seat billing. */
    capAtPurchasedSeats?: boolean;
    closeDialog: () => void;
}

export const InviteUsersDialog = WorkspaceController.withInitializedState(BillingController.withInitializedState(withFirebaseUser(React.forwardRef(
    function InviteUsersDialog(props: InviteUsersDialogProps & WorkspaceControllerState & BillingControllerState & { firebaseUser: FirebaseUser }, ref: React.Ref<any>) {
        const { cancelButtonText = t("Cancel"), showSelf = false, seatsInfoVariant = "default", capAtPurchasedSeats = false, closeDialog, workspace, users, userInvites, stripeData: { subscription }, firebaseUser } = props;

        const existingUserEmails = [
            ...users.map(u => u?.email?.toLowerCase?.()).filter(Boolean),
            ...userInvites.map(u => u?.email?.toLowerCase?.()).filter(Boolean)
        ] as string[];

        const currentUserEmail = firebaseUser.email.toLowerCase();

        const nonDeletableEmails = [currentUserEmail];

        const {
            members,
            handleEmailsChange,
            immediateTotal,
            nextInvoiceTotal,
            computedNextBillDate,
            isCalculatingTotals,
            hasError,
            setHasError,
            membersAreValid,
            additionalSeatCount,
            executeInvite,
            isInvitingSelf,
            filteredMembers
        } = useInviteMembersWithBilling({
            workspaceId: workspace.id,
            availableSeatCount: BillingController.availableSeatCount,
            paidSeatCount: BillingController.paidSeatCount,
            existingUserEmails,
            subscription
        });

        useEffect(() => {
            Analytics.ModifyWorkspaceUsers.InviteUsersStart({ workspace_id: workspace.id });

            ux.inviteUsersToWorkspace.register({
                workspaceId: workspace.id,
                tier: WorkspaceController.plan?.tier
            });

            return () => {
                ux.inviteUsersToWorkspace.abandon();
            };
        }, []);

        const canInvite = (BillingController.canChangeSubscriptionQuantity || additionalSeatCount <= 0) && membersAreValid && !isInvitingSelf;

        const paymentMethod = BillingController.paymentMethod;

        const handleInvite = async () => {
            ux.inviteUsersToWorkspace.update({ additionalSeatCount, invitedUserCount: filteredMembers.length });

            try {
                await executeInvite();
                ux.inviteUsersToWorkspace.complete();
                closeDialog();
            } catch (err) {
                logger.error(err, "[InviteUsersDialog] Failed to invite members");
                ux.inviteUsersToWorkspace.fail({ error: err }, undefined, true);

                ShowErrorDialog({
                    title: t("Failed to invite members"),
                    message: err.message
                });
            }
        };

        const handleEditPaymentMethod = () => {
            ShowDialog(ChangePaymentMethodDialog);
        };

        return (
            <BeautifulDialog closeDialog={closeDialog} ref={ref} data-test-id="invite-users-dialog">
                <StyledDialogTitle>
                    <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-logo-round.svg")} />
                    <div>{t("Invite team members")}</div>
                </StyledDialogTitle>
                <DialogContent>
                    <InviteSection
                        emails={showSelf ? [currentUserEmail, ...members.map(m => m.email)] : members.map(m => m.email)}
                        onEmailsChange={showSelf ? emails => handleEmailsChange(emails.filter(email => email !== currentUserEmail)) : handleEmailsChange}
                        nextBillDate={computedNextBillDate}
                        additionalSeatCount={additionalSeatCount}
                        totalDueToday={immediateTotal}
                        nextInvoiceTotal={nextInvoiceTotal}
                        isCalculating={isCalculatingTotals}
                        existingUserEmails={existingUserEmails}
                        hasError={hasError}
                        setHasError={setHasError}
                        nonDeletableEmails={nonDeletableEmails}
                        seatsInfoVariant={seatsInfoVariant}
                        maxEmailCount={capAtPurchasedSeats
                            ? BillingController.availableSeatCount + (showSelf ? 1 : 0)
                            : undefined}
                    />
                    {!capAtPurchasedSeats && !hasError && additionalSeatCount > 0 && BillingController.canChangeSubscriptionQuantity && paymentMethod && (
                        <>
                            <Gap30 />
                            <SectionTitle>{t("Payment method")}</SectionTitle>
                            <DefaultPaymentMethod
                                paymentMethod={paymentMethod}
                                actionLabel={t("Edit")}
                                onAction={handleEditPaymentMethod}
                            />
                        </>
                    )}
                </DialogContent>
                <StyledDialogActions>
                    <Button onClick={closeDialog} unframed large>
                        {cancelButtonText}
                    </Button>
                    <Button blue large onClick={handleInvite} disabled={!canInvite} data-test-id="invite-users-dialog-invite-button">
                        {t("Add members")}
                    </Button>
                </StyledDialogActions>
            </BeautifulDialog>
        );
    }
))));
